'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { Plus } from 'lucide-react';
import { resolveStorageUrl } from '@/lib/storage-url';

import {
  FeaturedEventItem,
  HeroAdminSettings,
  getStoredFeaturedEvents,
  getStoredHeroSettings,
} from '@/lib/featured-events';

export type TechHeroEventItem = {
  id?: string;
  cat: string;
  title: string;
  meta: string;
  img: string;
  slug?: string;
  w: number;
  h: number;
  x: number;
  y: number;
  rot: number;
  zIndex: number;
};

const DEFAULT_SLOTS: TechHeroEventItem[] = [
  {
    cat: 'CONFERENCE',
    title: 'Founders Summit',
    meta: 'MAR 14 · San Francisco',
    img: 'https://images.unsplash.com/photo-1540575467063-178a50c2df87?auto=format&fit=crop&w=400&q=70',
    w: 190,
    h: 245,
    x: 0,
    y: 10,
    rot: -9,
    zIndex: 6,
  },
  {
    cat: 'HACKATHON',
    title: 'Build Weekend',
    meta: 'APR 02 · Austin, TX',
    img: 'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=400&q=70',
    w: 170,
    h: 220,
    x: 200,
    y: 0,
    rot: 6,
    zIndex: 5,
  },
  {
    cat: 'PRODUCT LAUNCH',
    title: 'DevCon Keynote',
    meta: 'JUN 21 · New York',
    img: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?auto=format&fit=crop&w=400&q=70',
    w: 200,
    h: 255,
    x: 390,
    y: 40,
    rot: -5,
    zIndex: 4,
  },
  {
    cat: 'WORKSHOP',
    title: 'AI Engineering Lab',
    meta: 'MAY 09 · Brooklyn, NY',
    img: 'https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=400&q=70',
    w: 165,
    h: 210,
    x: 30,
    y: 280,
    rot: 5,
    zIndex: 4,
  },
  {
    cat: 'MEETUP',
    title: 'Startup Networking',
    meta: 'OCT 25 · Chicago',
    img: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?auto=format&fit=crop&w=400&q=70',
    w: 180,
    h: 230,
    x: 220,
    y: 250,
    rot: -7,
    zIndex: 5,
  },
  {
    cat: 'CONFERENCE',
    title: 'Cloud & Infra Summit',
    meta: 'JUL 11 · Seattle',
    img: 'https://images.unsplash.com/photo-1591115765373-5207764f72e7?auto=format&fit=crop&w=400&q=70',
    w: 190,
    h: 220,
    x: 420,
    y: 300,
    rot: 8,
    zIndex: 6,
  },
];

interface TechHeroSectionProps {
  createEventHref?: string;
  realEvents?: any[];
  ticketCount?: number | null;
}

export function TechHeroSection({
  createEventHref = '/events/create',
  realEvents = [],
  ticketCount = null,
}: TechHeroSectionProps) {
  const [storedEvents, setStoredEvents] = useState<FeaturedEventItem[]>([]);
  const [heroSettings, setHeroSettings] = useState<HeroAdminSettings>({
    ticketBadgeMode: 'auto',
    customTicketCount: 214,
  });

  useEffect(() => {
    const loadState = () => {
      setStoredEvents(getStoredFeaturedEvents());
      setHeroSettings(getStoredHeroSettings());
    };

    loadState();
    window.addEventListener('eh_hero_events_updated', loadState);
    return () => window.removeEventListener('eh_hero_events_updated', loadState);
  }, []);

  // Filter and sort featured events
  const activeFeatured = storedEvents
    .filter((e) => e.featured)
    .sort((a, b) => a.order - b.order);

  // Merge live platform events or custom stored featured events into the 6 designated cluster cards
  const displayCards: TechHeroEventItem[] = DEFAULT_SLOTS.map((slot, index) => {
    // Priority 1: Managed featured hero items from admin
    const featuredItem = activeFeatured[index];
    if (featuredItem) {
      const metaStr = [featuredItem.date, featuredItem.location].filter(Boolean).join(' · ');
      const resolvedCover = resolveStorageUrl(featuredItem.cover_image);

      return {
        ...slot,
        id: featuredItem.id,
        cat: featuredItem.category.toUpperCase(),
        title: featuredItem.title || slot.title,
        meta: metaStr || slot.meta,
        img: resolvedCover || featuredItem.cover_image || slot.img,
        slug: featuredItem.slug || undefined,
      };
    }

    // Priority 2: Real events passed from page props
    const matched = realEvents[index];
    if (matched) {
      let metaDate = '';
      if (matched.start_date) {
        try {
          const d = new Date(matched.start_date);
          metaDate = d.toLocaleDateString('en-US', { month: 'short', day: '2-digit' }).toUpperCase();
        } catch {
          metaDate = '';
        }
      }
      const location = matched.venue_city || (matched.mode === 'online' ? 'Online' : 'Global');
      const metaStr = [metaDate, location].filter(Boolean).join(' · ') || slot.meta;
      const resolvedCover = resolveStorageUrl(matched.cover_image);

      return {
        ...slot,
        id: String(matched.id || index),
        cat: (matched.type || matched.category || slot.cat).toUpperCase(),
        title: matched.title || slot.title,
        meta: metaStr,
        img: resolvedCover || slot.img,
        slug: matched.slug || undefined,
      };
    }

    // Fallback: Default slot
    return slot;
  });

  const effectiveCount =
    heroSettings.ticketBadgeMode === 'custom'
      ? heroSettings.customTicketCount
      : ticketCount && ticketCount > 0
        ? ticketCount
        : 214;

  const ticketsEyebrowText = `LIVE · ${effectiveCount.toLocaleString()} TICKETS BOOKED ${
    heroSettings.ticketBadgeMode === 'custom' ? 'THIS HOUR' : 'ON PLATFORM'
  }`;

  return (
    <div className="relative flex flex-col min-h-[100vh] w-full overflow-hidden bg-transparent">
      <style>{`
        @keyframes techHeroDrift {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-30px, 20px) scale(1.06); }
        }
        @keyframes techHeroPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(105, 124, 112, 0.6); }
          50% { box-shadow: 0 0 0 7px rgba(105, 124, 112, 0); }
        }
        @keyframes techHeroBob {
          0%, 100% { transform: translateY(0) rotate(var(--r, 0deg)); }
          50% { transform: translateY(-13px) rotate(var(--r, 0deg)); }
        }
        @keyframes techHeroMarquee {
          0% { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .tech-hero-drift,
          .tech-hero-pulse,
          .tech-hero-bob,
          .tech-hero-marquee {
            animation: none !important;
          }
        }
      `}</style>

      {/* ── HERO BANNER ────────────────────────────────────────── */}
      <section className="relative flex-1 w-full flex items-center overflow-hidden pt-24 pb-20 sm:pb-24 lg:py-0">
        {/* 1. MESH GRADIENT BLOBS (Layer z-[1], pure CSS, heavily blurred, asymmetric, 14-16% opacity) */}
        <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden" aria-hidden="true">
          {/* Top-left brand green blob (#697C70 / #5F6F52) */}
          <div
            className="absolute -left-28 -top-24 h-[540px] w-[540px] rounded-full filter blur-[120px] opacity-[0.16]"
            style={{
              background: 'radial-gradient(circle, #697C70 0%, rgba(105,124,112,0.4) 60%, transparent 80%)',
            }}
          />
          {/* Behind the event cards on the right (warm gold / amber accent #D4C59E & eucalyptus #98AA9D) */}
          <div
            className="absolute -right-24 top-[10%] h-[580px] w-[580px] rounded-full filter blur-[130px] opacity-[0.18]"
            style={{
              background: 'radial-gradient(circle, #D4C59E 0%, #98AA9D 50%, transparent 75%)',
            }}
          />
          {/* Lower-center subtle brand green blob */}
          <div
            className="absolute left-[35%] -bottom-28 h-[460px] w-[460px] rounded-full filter blur-[120px] opacity-[0.14]"
            style={{
              background: 'radial-gradient(circle, #697C70 0%, #B3C9D6 55%, transparent 80%)',
            }}
          />
        </div>

        {/* 2. DOT-GRID PATTERN (Layer z-[2], faint 1.5px dots, 28px spacing, ~5% opacity, soft linear-gradient edge mask) */}
        <div
          className="pointer-events-none absolute inset-0 z-[2] opacity-[0.055]"
          style={{
            maskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, black 15%, black 85%, transparent 100%)',
          }}
          aria-hidden="true"
        >
          <svg className="h-full w-full" xmlns="http://www.w3.org/2000/svg" width="100%" height="100%">
            <defs>
              <pattern id="hero-tech-dot-grid" width="28" height="28" patternUnits="userSpaceOnUse">
                <circle cx="2" cy="2" r="1" fill="#2D3536" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#hero-tech-dot-grid)" />
          </svg>
        </div>

        {/* 3. GRAIN / NOISE OVERLAY (Layer z-[3], pure SVG tiling turbulence, 2.8% opacity, mix-blend-multiply) */}
        <div
          className="pointer-events-none absolute inset-0 z-[3] opacity-[0.028] mix-blend-multiply"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
          aria-hidden="true"
        />

        {/* Inner Content Grid */}
        <div className="relative z-10 w-full max-w-[1500px] mx-auto px-4 sm:px-6 md:px-10 lg:px-12 flex flex-col lg:flex-row items-center justify-between gap-10 lg:gap-14">
          
          {/* Left Text Column */}
          <div className="w-full max-w-[540px] text-left shrink-0">
            {/* Eyebrow badge removed as requested */}

            {/* Headline: Each line on its own row, Georgia/serif, large scale, Soot color */}
            <h1
              className="text-[44px] leading-[1.0] sm:text-[56px] md:text-[64px] lg:text-[72px] font-medium tracking-[-0.02em] text-[#2D3536] mb-5 sm:mb-6"
              style={{ fontFamily: "Georgia, Cambria, 'Times New Roman', Times, serif" }}
            >
              <span className="block">Explore.</span>
              <span className="block">Register.</span>
              <span className="block">Experience.</span>
            </h1>

            {/* Subheading in Soot readable muted tone */}
            <p className="text-base sm:text-[17px] leading-relaxed text-[#2D3536]/80 max-w-[440px] mb-8 font-normal">
              Every tech event worth showing up for — conferences, hackathons, product launches, and meetups — in one platform built to move fast.
            </p>

            {/* Buttons: Solid dark Soot (#2D3536) primary and crisp border secondary */}
            <div className="flex flex-wrap sm:flex-nowrap items-center gap-3.5 sm:gap-4 font-sans">
              <Link
                href={createEventHref}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 sm:px-7 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-[15px] bg-[#2D3536] text-[#F2EFE2] shadow-[0_12px_28px_rgba(45,53,54,0.22)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#23292a] hover:shadow-[0_16px_34px_rgba(45,53,54,0.28)] active:translate-y-0"
              >
                <Plus className="w-4 h-4 text-[#F2EFE2]" strokeWidth={2.5} />
                <span>Create Event</span>
              </Link>
              <Link
                href="/events"
                className="w-full sm:w-auto inline-flex items-center justify-center px-6 sm:px-7 py-3.5 sm:py-4 rounded-xl font-bold text-sm sm:text-[15px] bg-bg/70 text-[#2D3536] border-[1.5px] border-[#2D3536]/25 shadow-xs transition-all duration-200 hover:-translate-y-0.5 hover:bg-bg hover:border-[#697C70] hover:text-[#697C70] active:translate-y-0"
              >
                Explore Events
              </Link>
            </div>
          </div>

          {/* Right Column — Dynamic Event Card Cluster */}
          <div className="w-full lg:w-[620px] flex justify-center lg:justify-end shrink-0 overflow-visible mt-6 lg:mt-0">
            <div
              className="relative w-[320px] h-[340px] sm:w-[480px] sm:h-[460px] lg:w-[620px] lg:h-[560px] shrink-0 overflow-visible"
              style={{
                perspective: '1000px',
              }}
            >
              {/* Responsive scaling container */}
              <div className="absolute top-0 left-1/2 -translate-x-1/2 lg:left-0 lg:translate-x-0 w-[620px] h-[560px] origin-top scale-[0.51] sm:scale-[0.77] lg:scale-100">
                {displayCards.map((item, i) => {
                  const linkHref = item.slug ? `/events/${item.slug}` : '/events';

                  return (
                    <Link
                      key={item.id || i}
                      href={linkHref}
                      className="tech-hero-bob absolute rounded-2xl bg-bg shadow-[0_22px_45px_rgba(45,53,54,0.16)] border border-[#E6E2D7] overflow-hidden text-[#2D3536] transition-all duration-300 hover:scale-105 hover:border-[#98AA9D] hover:shadow-[0_28px_56px_rgba(45,53,54,0.22)] hover:!z-30 cursor-pointer group block"
                      style={{
                        width: `${item.w}px`,
                        height: `${item.h}px`,
                        left: `${item.x}px`,
                        top: `${item.y}px`,
                        zIndex: item.zIndex,
                        // @ts-ignore
                        '--r': `${item.rot}deg`,
                        transform: `rotate(${item.rot}deg)`,
                        animation: `techHeroBob 6s ease-in-out infinite`,
                        animationDelay: `${i * 0.7}s`,
                      }}
                    >
                      {/* 60% top thumbnail photo */}
                      <div
                        className="w-full h-[60%] bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                        style={{
                          backgroundImage: `url('${item.img}')`,
                          backgroundColor: '#ffffff',
                        }}
                      />
                      {/* Card Body */}
                      <div className="p-3 sm:p-3.5 flex flex-col justify-between h-[40%] bg-bg">
                        <div>
                          <div className="text-[9px] font-bold tracking-[0.1em] text-[#697C70] uppercase truncate">
                            {item.cat}
                          </div>
                          <div
                            className="text-[15px] font-medium leading-tight text-[#2D3536] mt-0.5 line-clamp-1 group-hover:text-[#697C70] transition-colors"
                            style={{ fontFamily: "Georgia, Cambria, 'Times New Roman', Times, serif" }}
                          >
                            {item.title}
                          </div>
                        </div>
                        <div className="text-[10.5px] text-[#697C70]/90 truncate font-sans">
                          {item.meta}
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ── 4. SCROLLING CATEGORY MARQUEE (Moss #697C70 with Plaster #F2EFE2 text) ──────────────────────── */}
      <div className="absolute bottom-0 z-10 w-full overflow-hidden bg-[#697C70] py-3.5 sm:py-4 shadow-sm border-y border-[#5A6B5E] select-none">
        <div
          className="tech-hero-marquee flex w-max items-center whitespace-nowrap will-change-transform"
          style={{ animation: 'techHeroMarquee 36s linear infinite' }}
        >
          {/* First set */}
          <div className="flex shrink-0 items-center font-sans font-bold text-sm sm:text-[15px] tracking-[0.04em] text-[#F2EFE2]">
            <span className="mx-6 sm:mx-7">★ CONFERENCES</span>
            <span className="mx-6 sm:mx-7">★ HACKATHONS</span>
            <span className="mx-6 sm:mx-7">★ PRODUCT LAUNCHES</span>
            <span className="mx-6 sm:mx-7">★ WORKSHOPS</span>
            <span className="mx-6 sm:mx-7">★ MEETUPS</span>
            <span className="mx-6 sm:mx-7">★ SUMMITS</span>
            <span className="mx-6 sm:mx-7">★ MUN</span>
            <span className="mx-6 sm:mx-7">★ CONFERENCE</span>
            <span className="mx-6 sm:mx-7">★ IDEATHON</span>
            <span className="mx-6 sm:mx-7">★ PAPER PRESENTATION</span>
            <span className="mx-6 sm:mx-7">★ SEMINAR</span>
            <span className="mx-6 sm:mx-7">★ BOOTCAMP</span>
            <span className="mx-6 sm:mx-7">★ QUIZ</span>
            <span className="mx-6 sm:mx-7">★ CODING CONTEST</span>
          </div>
          {/* Duplicate set for seamless infinite loop */}
          <div className="flex shrink-0 items-center font-sans font-bold text-sm sm:text-[15px] tracking-[0.04em] text-[#F2EFE2]" aria-hidden="true">
            <span className="mx-6 sm:mx-7">★ CONFERENCES</span>
            <span className="mx-6 sm:mx-7">★ HACKATHONS</span>
            <span className="mx-6 sm:mx-7">★ PRODUCT LAUNCHES</span>
            <span className="mx-6 sm:mx-7">★ WORKSHOPS</span>
            <span className="mx-6 sm:mx-7">★ MEETUPS</span>
            <span className="mx-6 sm:mx-7">★ SUMMITS</span>
            <span className="mx-6 sm:mx-7">★ MUN</span>
            <span className="mx-6 sm:mx-7">★ CONFERENCE</span>
            <span className="mx-6 sm:mx-7">★ IDEATHON</span>
            <span className="mx-6 sm:mx-7">★ PAPER PRESENTATION</span>
            <span className="mx-6 sm:mx-7">★ SEMINAR</span>
            <span className="mx-6 sm:mx-7">★ BOOTCAMP</span>
            <span className="mx-6 sm:mx-7">★ QUIZ</span>
            <span className="mx-6 sm:mx-7">★ CODING CONTEST</span>
          </div>
        </div>
      </div>
    </div>
  );
}
